/**
 * Builds firmCaseContext JSON for chat/disc (legal discussion)/pleadings sends.
 * Truncates long text and caps document list size (must match server limits).
 *
 * IMPORTANT: Always produces a full key set so JSON.stringify never drops fields
 * (undefined values are omitted by JSON.stringify — we use '' and [] instead).
 */

export const FIRM_CONTEXT_SHORT_DESC_MAX = 4000;
export const FIRM_CONTEXT_SUMMARY_MAX = 8000;
export const FIRM_CONTEXT_DESCRIPTION_MAX = 16000;
export const FIRM_CONTEXT_DOCUMENTS_MAX = 20;
export const FIRM_CONTEXT_STRING_FIELD_MAX = 2000;

/** Exported for FirmModeContext storage caps */
export function trimStr(val, max) {
  if (val == null) return '';
  const s = typeof val === 'string' ? val : String(val);
  if (s.length <= max) return s;
  return s.slice(0, max);
}

/**
 * @param {unknown} rawDocs - from API or activeFirmCase.documents
 * @returns {{ id: string, name: string, documentType: string, createdAt: string }[]}
 */
export function trimDocumentsMetadata(rawDocs) {
  if (!Array.isArray(rawDocs)) return [];
  const out = [];
  for (let i = 0; i < rawDocs.length && out.length < FIRM_CONTEXT_DOCUMENTS_MAX; i++) {
    const d = rawDocs[i];
    if (!d || typeof d !== 'object') continue;
    const id = trimStr(d.id, 128);
    if (!id) continue;
    out.push({
      id,
      name: trimStr(d.name, 512),
      documentType: trimStr(d.documentType, 128),
      createdAt: trimStr(d.createdAt, 64),
    });
  }
  return out;
}

/**
 * Merge a partial case object (e.g. from sessionStorage or API) into the full in-memory shape.
 * Ensures every field exists so sends never lose keys to undefined / old 7-field blobs.
 * @param {object|null|undefined} src
 * @returns {object|null}
 */
export function mergeActiveFirmCaseFromPartial(src) {
  if (!src || src.id == null || src.id === '') return null;
  const id = String(src.id).trim();
  if (!id) return null;

  return {
    id,
    title: src.title != null ? String(src.title) : '',
    caseNumber: src.caseNumber != null ? String(src.caseNumber) : '',
    fileNumber: src.fileNumber != null ? String(src.fileNumber) : '',
    status: src.status != null ? String(src.status) : '',
    court: src.court != null ? String(src.court) : '',
    city: src.city != null ? String(src.city) : '',
    clientId:
      src.clientId === undefined || src.clientId === null || src.clientId === ''
        ? null
        : String(src.clientId),
    stage: src.stage != null ? String(src.stage) : '',
    priority: src.priority != null ? String(src.priority) : '',
    shortDescription: src.shortDescription != null ? String(src.shortDescription) : '',
    summary: src.summary != null ? String(src.summary) : '',
    description:
      src.description != null && src.description !== ''
        ? String(src.description)
        : src.fullDescription != null && src.fullDescription !== ''
          ? String(src.fullDescription)
          : '',
    openedAt: src.openedAt != null ? String(src.openedAt) : '',
    closedAt: src.closedAt != null ? String(src.closedAt) : '',
    documents: trimDocumentsMetadata(src.documents),
  };
}

/**
 * @param {object|null|undefined} activeFirmCase - from FirmModeContext (includes optional documents[])
 * @returns {object|null} - payload for JSON.stringify or null if no valid case id
 */
export function buildFirmCaseContextPayload(activeFirmCase) {
  const row = mergeActiveFirmCaseFromPartial(activeFirmCase);
  if (!row) return null;

  const payload = {
    id: row.id,
    title: trimStr(row.title, FIRM_CONTEXT_STRING_FIELD_MAX),
    caseNumber: trimStr(row.caseNumber, 256),
    status: trimStr(row.status, 128),
    court: trimStr(row.court, FIRM_CONTEXT_STRING_FIELD_MAX),
    city: trimStr(row.city, 512),
    clientId: row.clientId,
    fileNumber: trimStr(row.fileNumber, 256),
    stage: trimStr(row.stage, 256),
    priority: trimStr(row.priority, 64),
    shortDescription: trimStr(row.shortDescription, FIRM_CONTEXT_SHORT_DESC_MAX),
    summary: trimStr(row.summary, FIRM_CONTEXT_SUMMARY_MAX),
    description: trimStr(row.description, FIRM_CONTEXT_DESCRIPTION_MAX),
    openedAt: row.openedAt !== '' ? trimStr(row.openedAt, 64) : '',
    closedAt: row.closedAt !== '' ? trimStr(row.closedAt, 64) : '',
    documents: trimDocumentsMetadata(row.documents),
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('[firmCaseContext] final payload keys', Object.keys(payload).sort());
    console.log('[firmCaseContext] final payload', payload);
  }

  return payload;
}
